<template>
  <div class="news-page">
    <PageHeroBanner title="最新动态" title-en="NEWS" :image="bannerBg" />

    <div class="container page-sub">
      <p class="breadcrumb">
        <router-link to="/">首页</router-link>
        <span class="breadcrumb__sep">/</span>
        <span class="breadcrumb__here">最新动态</span>
      </p>
      <p class="page-lead">打造图书馆线上线下融合模式，推动智慧化、数字化服务升级</p>
    </div>

    <div class="container page-layout">
      <aside class="side-nav" aria-label="新闻分类">
        <button
          v-for="cat in categories"
          :key="String(cat.id)"
          type="button"
          class="side-nav__btn"
          :class="{ active: String(cat.id) === String(activeCategoryId) }"
          @click="selectCategory(cat.id)"
        >
          <span class="side-nav__name">{{ cat.name || cat.title || '-' }}</span>
        </button>
      </aside>

      <div class="main">
        <div v-if="loading" class="state state--loading">加载中…</div>
        <div v-else-if="!newsList.length" class="state">暂无新闻数据</div>
        <div v-else class="news-grid">
          <article v-for="(item, idx) in newsList" :key="item.id" class="news-card">
            <router-link :to="detailRoute(item)" class="news-link">
              <div
                class="news-card__media"
                :class="{ 'news-card__media--ready': !item.image || imageReady[item.id] }"
              >
                <img
                  v-if="item.image"
                  class="news-card__img"
                  :src="item.image"
                  :alt="item.title"
                  :loading="idx < 6 ? 'eager' : 'lazy'"
                  decoding="async"
                  :fetchpriority="idx === 0 ? 'high' : idx < 6 ? 'auto' : 'low'"
                  @load="onNewsImageLoad(item.id)"
                  @error="onNewsImageLoad(item.id)"
                >
                <div v-else class="news-card__ph" aria-hidden="true">{{ shortTitle(item.title) }}</div>
              </div>
              <div class="news-card__body">
                <time v-if="item.date" class="news-date">{{ item.date }}</time>
                <h2 class="news-title">{{ item.title }}</h2>
                <p v-if="item.subtitle" class="news-sub">{{ item.subtitle }}</p>
                <span class="news-more">查看详情 &gt;&gt;</span>
              </div>
            </router-link>
          </article>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchNewsCategories, fetchNewsByCategory } from '../api/news'
import PageHeroBanner from '../components/PageHeroBanner.vue'
import { PAGE_BANNER_BG } from '../constants/pageBannerImages'

export default {
  name: 'NewsCenterView',
  components: { PageHeroBanner },
  data() {
    return {
      bannerBg: PAGE_BANNER_BG.news,
      categories: [],
      activeCategoryId: 'all',
      newsList: [],
      loading: false,
      imageReady: {}
    }
  },
  watch: {
    '$route.query.category': {
      immediate: true,
      handler(category) {
        if (!this.categories.length) return
        const cid =
          category !== undefined && category !== null && category !== ''
            ? String(category)
            : 'all'
        this.activeCategoryId = cid
        this.loadNews(cid)
      }
    }
  },
  async mounted() {
    this.loading = true
    try {
      const list = await fetchNewsCategories()
      this.categories = Array.isArray(list) ? list : []
      const fromQuery = this.$route.query.category
      const first = this.categories[0]
      const firstId = first != null ? String(first.id) : 'all'
      this.activeCategoryId =
        fromQuery !== undefined && fromQuery !== null && fromQuery !== ''
          ? String(fromQuery)
          : firstId
      await this.loadNews(this.activeCategoryId)
    } finally {
      this.loading = false
    }
  },
  methods: {
    async loadNews(categoryId) {
      this.loading = true
      this.imageReady = {}
      try {
        const list = await fetchNewsByCategory(categoryId)
        this.newsList = Array.isArray(list) ? list : []
      } finally {
        this.loading = false
      }
    },
    shortTitle(t) {
      if (!t || typeof t !== 'string') return '—'
      return t.length <= 4 ? t : t.slice(0, 2)
    },
    onNewsImageLoad(id) {
      if (id == null) return
      this.imageReady = { ...this.imageReady, [id]: true }
    },
    /** 左侧分类 id（含 all=全部）→ GET /api/v1/news 带分类参数，刷新右侧列表 */
    selectCategory(id) {
      const cid = String(id)
      if (cid === String(this.activeCategoryId)) return
      this.activeCategoryId = cid
      const query = cid === 'all' ? {} : { category: cid }
      this.$router.replace({ path: '/news', query })
    },
    detailRoute(item) {
      const q =
        this.activeCategoryId && this.activeCategoryId !== 'all'
          ? { category: String(this.activeCategoryId) }
          : {}
      return {
        name: 'news-detail',
        params: { id: String(item.id) },
        query: q
      }
    }
  }
}
</script>

<style scoped>
.news-page { padding-top: 0; min-height: 100vh; background: #f5f9ff; }
.page-sub { padding: 20px 20px 8px; }
.breadcrumb { font-size: 14px; color: #64748b; margin: 0 0 8px; }
.breadcrumb a { color: #2563eb; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb__sep { margin: 0 8px; color: #cbd5e1; }
.breadcrumb__here { color: #334155; }
.page-lead { font-size: 15px; color: #64748b; margin: 0 0 20px; }
.page-layout { display: grid; grid-template-columns: 220px 1fr; gap: 22px; align-items: start; }
.main { min-height: 320px; }
.side-nav { background: #fff; border: 1px solid #dbe7f5; border-radius: 10px; padding: 10px 0; box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04); }
.side-nav__btn { width: 100%; border: none; background: transparent; cursor: pointer; text-align: left; padding: 14px 16px; border-left: 3px solid transparent; }
.side-nav__btn:hover { background: #f8fbff; }
.side-nav__btn.active { background: #edf5ff; border-left-color: #2563eb; }
.side-nav__name { font-size: 15px; color: #0f172a; font-weight: 600; }
.news-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; padding-bottom: 80px; }
.news-card { background: #fff; border: 1px solid #dbe7f5; border-radius: 10px; overflow: hidden; transition: box-shadow 0.25s, transform 0.25s; }
.news-card:hover { box-shadow: 0 16px 40px rgba(37, 99, 235, 0.12); transform: translateY(-4px); }
.news-link { display: block; color: inherit; }
.news-card__media {
  aspect-ratio: 16 / 10;
  background: #e2e8f0;
  overflow: hidden;
  position: relative;
}
.news-card__media::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.5) 45%, transparent 90%);
  background-size: 220% 100%;
  animation: news-card-shimmer 1.35s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
}
.news-card__media--ready::after { display: none; }
.news-card__img {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.35s ease;
}
.news-card__media--ready .news-card__img { opacity: 1; }
@keyframes news-card-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.news-card__ph {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #93c5fd 0%, #3b82f6 100%);
  color: #fff;
  font-size: 22px;
  font-weight: 700;
}
.news-card__body { padding: 16px 18px 20px; }
.news-date { display: inline-block; font-size: 13px; color: #2563eb; font-weight: 600; margin-bottom: 8px; }
.news-title { font-size: 17px; color: #0f172a; font-weight: 600; line-height: 1.45; margin: 0 0 8px; }
.news-sub { font-size: 14px; color: #64748b; line-height: 1.65; margin: 0 0 10px; }
.news-more { font-size: 13px; color: #2563eb; font-weight: 500; }
.state { padding: 48px; text-align: center; color: #64748b; background: #fff; border-radius: 10px; border: 1px dashed #cbd5e1; margin-bottom: 80px; }
.state--loading { color: #2563eb; }
@media (max-width: 1024px) { .news-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) {
  .page-layout { grid-template-columns: 1fr; }
  .side-nav { display: flex; flex-wrap: wrap; gap: 8px; padding: 12px; }
  .side-nav__btn { width: auto; border-left: none; border: 1px solid #dbe7f5; border-radius: 8px; padding: 10px 12px; }
  .side-nav__btn.active { border-color: #2563eb; }
  .news-grid { grid-template-columns: 1fr; }
}
</style>
